// src/pages/AdminUsers.jsx
import { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase/firebase.js";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";

const ROLES = ["admin", "sucursal", "operaciones", "agencia"];

export default function AdminUsers() {
  // Solo para UI
  const [me, setMe] = useState(null);

  // Catálogo de sucursales [{id, nombre}]
  const [sucursales, setSucursales] = useState([]);

  // Listado/selección
  const [users, setUsers] = useState([]);
  const [selId, setSelId] = useState(""); // uid del doc seleccionado (vacío => nuevo)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Búsqueda
  const [search, setSearch] = useState("");

  // Form (usa canónicos sucursalId/sucursalNombre; mantiene `sucursal` legacy al guardar)
  const [form, setForm] = useState({
    uid: "",
    email: "",
    nombre: "",
    rol: "agencia",
    sucursalId: "",
    sucursalNombre: "",
    // espejo legacy (no editable en UI, lo llenamos al guardar):
    // sucursal: "",
    agenciaId: "",
    activo: true,
    creditosPublicacion: 0,
    kycAprobado: false,
  });

  // ---- Helpers
  const nombreDeSucursal = (id) => {
    if (!id) return "";
    const s = sucursales.find((x) => x.id === id);
    return s?.nombre || id;
  };

  // Cargar usuario actual (UI)
  useEffect(() => {
    const u = auth.currentUser;
    if (u) setMe({ uid: u.uid, email: u.email || "" });
    else setMe(null);
  }, []);

  // Cargar catálogo de sucursales
  const cargarSucursales = async () => {
    try {
      const snap = await getDocs(collection(db, "sucursales"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Orden por nombre
      list.sort((a, b) => (a.nombre || a.id).localeCompare(b.nombre || b.id));
      setSucursales(list);
    } catch (e) {
      console.error("[AdminUsers] sucursales:", e);
    }
  };

  // Cargar listado de usuarios
  const cargarUsuarios = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "users"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Orden: por nombre/email
      list.sort((a, b) => {
        const A = (a.nombre || a.email || a.id || "").toLowerCase();
        const B = (b.nombre || b.email || b.id || "").toLowerCase();
        return A.localeCompare(B);
      });
      setUsers(list);

      if (list.length > 0) {
        const u = list[0];
        // Adaptar legacy → canónico al mostrar
        setSelId(u.id);
        setForm({
          uid: u.id,
          email: u.email || "",
          nombre: u.nombre || "",
          rol: u.rol || "agencia",
          sucursalId: u.sucursalId || "", // preferente
          sucursalNombre:
            u.sucursalNombre ||
            (u.sucursalId ? nombreDeSucursal(u.sucursalId) : (u.sucursal || "")),
          agenciaId: u.agenciaId || "",
          activo: u.activo !== false,
          creditosPublicacion: Number(u.creditosPublicacion) || 0,
          kycAprobado: !!u.kycAprobado,
        });
      } else {
        limpiar();
      }
    } catch (e) {
      console.error("[AdminUsers] cargar:", e);
      alert("No se pudo cargar el listado de usuarios.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // cargar catálogos y usuarios
    (async () => {
      await cargarSucursales();
      await cargarUsuarios();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cambio de selección
  useEffect(() => {
    if (!selId) return; // nuevo
    const u = users.find((x) => x.id === selId);
    if (!u) return;
    setForm({
      uid: u.id,
      email: u.email || "",
      nombre: u.nombre || "",
      rol: u.rol || "agencia",
      sucursalId: u.sucursalId || "",
      sucursalNombre:
        u.sucursalNombre ||
        (u.sucursalId ? nombreDeSucursal(u.sucursalId) : (u.sucursal || "")),
      agenciaId: u.agenciaId || "",
      activo: u.activo !== false,
      creditosPublicacion: Number(u.creditosPublicacion) || 0,
      kycAprobado: !!u.kycAprobado,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId, users, sucursales]);

  const esNuevo = useMemo(() => selId === "", [selId]);

  const limpiar = () => {
    setSelId("");
    setForm({
      uid: "",
      email: "",
      nombre: "",
      rol: "agencia",
      sucursalId: "",
      sucursalNombre: "",
      agenciaId: "",
      activo: true,
      creditosPublicacion: 0,
      kycAprobado: false,
    });
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    // si cambian el rol, limpiamos campos dependientes
    if (name === "rol") {
      const nextRol = value;
      setForm((v) => ({
        ...v,
        rol: nextRol,
        // si pasa a agencia, no queremos sucursal asignada
        sucursalId: nextRol === "agencia" ? "" : v.sucursalId,
        sucursalNombre: nextRol === "agencia" ? "" : v.sucursalNombre,
        // si pasa a sucursal/operaciones, limpiamos agenciaId
        agenciaId: nextRol === "agencia" ? v.agenciaId : "",
      }));
      return;
    }

    if (name === "sucursalId") {
      const sid = value;
      const nombre = nombreDeSucursal(sid);
      setForm((v) => ({ ...v, sucursalId: sid, sucursalNombre: nombre }));
      return;
    }

    setForm((v) => ({
      ...v,
      [name]:
        type === "checkbox"
          ? checked
          : name === "creditosPublicacion"
          ? Number(value)
          : value,
    }));
  };

  const validarForm = () => {
    const uid = (form.uid || "").trim();
    const email = (form.email || "").trim();

    if (esNuevo && !uid) {
      alert("UID es obligatorio para crear el documento (debe coincidir con Auth).");
      return false;
    }
    if (!email) {
      alert("Email es obligatorio.");
      return false;
    }
    if (!ROLES.includes(form.rol)) {
      alert("Rol inválido.");
      return false;
    }
    if ((form.rol === "sucursal" || form.rol === "operaciones") && !form.sucursalId) {
      alert("Selecciona una sucursal (ID) para este rol.");
      return false;
    }
    if (form.rol === "agencia" && !form.agenciaId) {
      alert("agenciaId es obligatorio para rol 'agencia'.");
      return false;
    }
    return true;
  };

  const guardar = async () => {
    try {
      if (!validarForm()) return;
      setSaving(true);

      const uid = (form.uid || "").trim();
      const email = (form.email || "").trim();
      const idDoc = esNuevo ? uid : selId;

      // Normalizar canónicos
      const sucursalId = (form.sucursalId || "").trim();
      const sucursalNombre =
        (form.sucursalNombre || nombreDeSucursal(sucursalId) || "").trim();

      const payload = {
        email,
        nombre: (form.nombre || "").trim(),
        rol: form.rol,
        // canónicos
        sucursalId: sucursalId || "",
        sucursalNombre: sucursalNombre || "",
        // espejo legacy temporal
        sucursal: sucursalId ? sucursalNombre : "",
        // agencia
        agenciaId: (form.agenciaId || "").trim() || null,
        // demás
        activo: !!form.activo,
        creditosPublicacion: Number.isFinite(form.creditosPublicacion)
          ? form.creditosPublicacion
          : 0,
        kycAprobado: !!form.kycAprobado,
        updatedAt: new Date().toISOString(),
      };

      const ref = doc(db, "users", idDoc);
      if (esNuevo) {
        await setDoc(ref, {
          ...payload,
          fechaCreacion: new Date().toISOString(),
        });
      } else {
        await updateDoc(ref, payload);
      }

      alert("Usuario guardado.");
      await cargarUsuarios();
      setSelId(idDoc);
    } catch (e) {
      console.error("[AdminUsers] guardar:", e);
      alert(e.message || "No se pudo guardar el usuario.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (activo) => {
    if (!selId) return;
    try {
      setSaving(true);
      await updateDoc(doc(db, "users", selId), {
        activo: !!activo,
        updatedAt: new Date().toISOString(),
      });
      alert(activo ? "Usuario activado." : "Usuario desactivado.");
      await cargarUsuarios();
      setSelId(selId);
    } catch (e) {
      console.error("[AdminUsers] toggleActivo:", e);
      alert("No se pudo cambiar el estado del usuario.");
    } finally {
      setSaving(false);
    }
  };

  // Filtro por búsqueda
  const listFiltrada = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const target = [
        u.id,
        u.email,
        u.nombre,
        u.rol,
        u.sucursalNombre || u.sucursal || "",
        u.sucursalId || "",
        u.agenciaId || "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return target.includes(q);
    });
  }, [users, search]);

  if (loading) return <div className="p-6 text-gray-600">Cargando…</div>;

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      {/* Topbar */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex justify-between items-center">
        <div className="font-semibold">Administrar Usuarios</div>
        <div className="text-sm text-gray-600">Admin · {me?.email || ""}</div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista */}
        <aside className="lg:col-span-1">
          <div className="rounded border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h3 className="font-semibold">Usuarios</h3>
              <button
                onClick={limpiar}
                className="px-2 py-1 rounded bg-gray-900 text-white text-sm hover:bg-gray-800"
              >
                + Nuevo
              </button>
            </div>

            <input
              placeholder="Buscar… (nombre, email, rol, sucursal…) "
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full mb-2 px-3 py-2 rounded border border-gray-300 bg-gray-50"
            />

            <div className="space-y-1 max-h-[60vh] overflow-auto">
              {listFiltrada.map((u) => (
                <button
                  key={u.id}
                  onClick={() => setSelId(u.id)}
                  className={`w-full text-left px-3 py-2 rounded border ${
                    selId === u.id
                      ? "border-gray-900 bg-gray-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="font-medium">
                    {u.nombre || u.email || u.id}
                  </div>
                  <div className="text-xs text-gray-500">
                    {u.email} · {u.rol || "—"} · {u.sucursalNombre || u.sucursal || "—"} ·{" "}
                    {u.activo !== false ? "Activo" : "Inactivo"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Form */}
        <main className="lg:col-span-2">
          <div className="rounded border border-gray-200 bg-white p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-sm text-gray-600">UID (doc id)</label>
                <input
                  name="uid"
                  value={form.uid}
                  onChange={onChange}
                  disabled={!esNuevo}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  placeholder="Pega aquí el UID de Auth"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-gray-600">Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  placeholder="usuario@dominio.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="text-sm text-gray-600">Nombre</label>
                <input
                  name="nombre"
                  value={form.nombre}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Rol</label>
                <select
                  name="rol"
                  value={form.rol}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sucursal/Agencia según rol */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-sm text-gray-600">Sucursal (ID)</label>
                <select
                  name="sucursalId"
                  value={form.sucursalId}
                  onChange={onChange}
                  disabled={form.rol === "agencia"} // agencias no usan sucursalId
                  className={`mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 ${
                    form.rol === "agencia" ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">—</option>
                  {sucursales.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.id} — {s.nombre || s.id}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Sucursal (nombre)</label>
                <input
                  name="sucursalNombre"
                  value={form.sucursalNombre}
                  onChange={onChange}
                  disabled={form.rol === "agencia"}
                  className={`mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 ${
                    form.rol === "agencia" ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  placeholder="Se autollenará al elegir el ID"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">agenciaId</label>
                <input
                  name="agenciaId"
                  value={form.agenciaId}
                  onChange={onChange}
                  disabled={form.rol !== "agencia"}
                  className={`mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 ${
                    form.rol !== "agencia" ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  placeholder="motors_rhm / …"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="flex items-end">
                <label className="text-sm text-gray-600 mr-2">Activo</label>
                <input
                  type="checkbox"
                  name="activo"
                  checked={!!form.activo}
                  onChange={onChange}
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">
                  Créditos publicación
                </label>
                <input
                  type="number"
                  name="creditosPublicacion"
                  value={form.creditosPublicacion}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>

              <div className="flex items-end">
                <label className="text-sm text-gray-600 mr-2">KYC aprobado</label>
                <input
                  type="checkbox"
                  name="kycAprobado"
                  checked={!!form.kycAprobado}
                  onChange={onChange}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={guardar}
                disabled={saving}
                className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {saving ? "Guardando…" : esNuevo ? "Crear usuario" : "Guardar cambios"}
              </button>

              {!esNuevo && (
                <>
                  <button
                    onClick={() => toggleActivo(false)}
                    disabled={saving || form.activo === false}
                    className="px-3 py-2 rounded border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    Desactivar
                  </button>
                  <button
                    onClick={() => toggleActivo(true)}
                    disabled={saving || form.activo === true}
                    className="px-3 py-2 rounded border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    Activar
                  </button>
                  <span className="text-sm text-gray-600">
                    UID: <code>{selId}</code>
                  </span>
                </>
              )}
            </div>

            {esNuevo && (
              <p className="text-xs text-gray-500">
                Recuerda: este formulario no crea cuentas en Firebase Auth. Primero crea el
                usuario en Auth, copia su UID aquí y guarda para crear/ligar el documento.
              </p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}